'use strict';

var Debouncer = require('./Debouncer.js');

// TODO See the TODO inside the _onDomModify()

/**
 * @class
 * @classdesc
 * Monitors for the addition or removal of elements form the DOM.
 * It uses MutationObserver for new browsers and DOMSubtreeModified for old browsers.
 * It may be possible to create an endless loop (especially in older browsers), so be
 * careful to not create or remove the same instance being monitored for with code
 * watching for the same thing.
 * 
 * Example use:
 * <pre><code>
 * DocumentObserver.getInstance().onStyleClass(
 * 	styleClass,
 * 	this._initComponentInstances.bind(this, styleClass),
 * 	this._destroyComponentInstances.bind(this, styleClass)
 * );
 * </code></pre>
 * 
 * Another example use:
 * <pre><code>
 * DocumentObserver.getInstance().onStyleClass(
 * 	styleClass,
 * 	this.observe,
 * 	this.unobserve
 * );
 * </code></pre>
 * 
 * @param {Element} [docElem] - This is the document.documentElement
 */
var DocumentObserver = function(docElem) {
	this._docElem = docElem;
	
	this.destroy = this.destroy.bind(this);
	this._onDomModify = this._onDomModify.bind(this);
	
	if (window.MutationObserver) {
		
		// Newer browsers
		this._onDomModifyDebouncer = new Debouncer(this._onDomModify);
		this._observer = new MutationObserver(this._onDomModifyDebouncer.run);
		
		this._observe = function() {
			this._observer.observe(this._docElem, {
				childList : true,
				subtree : true,
				attributes : false
			});
		}
		this._unobserve = function() {
			this._observer.disconnect();
		}
		
	} else {

		// Older browsers
		// TODO Test this. And is my debounce period long enough?
		this._onDomModifyDebouncer = new Debouncer(this._onDomModify, 120);
		
		this._observe = function() {
			this._docElem.addEventListener('DOMSubtreeModified', this._onDomModifyDebouncer.run, false);
		}
		this._unobserve = function() {
			this._docElem.removeEventListener('DOMSubtreeModified', this._onDomModifyDebouncer.run, false);
		}
		
	}
	this._observe();
		// TODO Will an endless loop happen (especially in older browsers)? If not we probably can get rid of this method and call the stuff above. Otherwise we probably need to unobserve and reobserve after listeners are done.
		// TODO Everything working? If not, consider a polyfill: https://github.com/megawac/MutationObserver.js/tree/master
}

DocumentObserver._instance = null;

/**
 * @public
 * @param {string} styleClass - 
 * @param {Function} onAdd - Called with element(s) are added. Provided one argument which is an array of element(s). 
 * @param {Function} onRemove - Sane as onAdd(), but for removed elements.
 */
DocumentObserver.prototype.onStyleClass = function(styleClass, onAdd, onRemove) {
	var elementListListener = new ElementListListener(this._docElem, styleClass, onAdd, onRemove);
	if (this._onStyleClassListeners === undefined) {
		this._onStyleClassListeners = new Array();
	}
	this._onStyleClassListeners.push(elementListListener);
	elementListListener.notify(); // Call once because there may be some existing elements already.
}


/**
 * @private
 */
DocumentObserver.prototype._onDomModify = function() {
	// TODO We should drop the fallback and only use the mutationobserver. And make this take an argument so the browser tells us what changes instead of us having to find it and record what was changed.
	//		See: https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver/MutationObserver
	//		And: https://developer.mozilla.org/en-US/docs/Web/API/MutationRecord
	//		Also the options passed to observer() when setting up could MAYBE be more specific: https://developer.mozilla.org/en-US/docs/Web/API/MutationObserverInit
	if (this._onStyleClassListeners !== undefined) {
		for (var i = 0, len = this._onStyleClassListeners.length; i < len; i++) {
			this._onStyleClassListeners[i].notify();
		}
	}
}


/**
 * @public
 */
DocumentObserver.prototype.destroy = function () {
	this._unobserve();
	delete this._docElment;
	DocumentObserver._instance = null;
};

/**
 * @public
 */
function getInstance() {
	if (DocumentObserver._instance === null) {
		DocumentObserver._instance = new DocumentObserver(document.documentElement);
	}
	return DocumentObserver._instance;
}


// =================== ElementListListener (private class) =================
/**
 * @private
 * @class
 * To help keep track of and determine what elements have changed over time.
 */
var _ElementListListenerId = 0;
var ElementListListener = function(docElem, styleClass, onAdd, onRemove) {
	this._id = _ElementListListenerId++;
	this._liveCollection = docElem.getElementsByClassName(styleClass);
	this._onAdd = onAdd;
	this._onRemove = onRemove;
}

var LISTENER_PROCESSED_ELEMENT_DATASET_KEY_PREFIX = 'documentChangedIsProcessed_';
ElementListListener.prototype._getElementProcessedIndex = function(elem) {
	return elem.dataset[LISTENER_PROCESSED_ELEMENT_DATASET_KEY_PREFIX + this._id];
}
ElementListListener.prototype._setElementProcessedIndex = function(elem, val) {
	elem.dataset[LISTENER_PROCESSED_ELEMENT_DATASET_KEY_PREFIX + this._id] = val;
}
ElementListListener.prototype._clearElementProcessedIndex = function(elem) {
	delete elem.dataset[LISTENER_PROCESSED_ELEMENT_DATASET_KEY_PREFIX + this._id];
}

ElementListListener.prototype.notify = function() {
	// Prepare buckets
	var addedElems = new Array(),
		removedElems,
		lastProcessedElems = this._processedElems || new Array();
	this._processedElems = new Array();

	// Processes the liveCollection into the buckets
	for (var i = 0, len = this._liveCollection.length, elem, processedIndex; i < len; i++) {
		elem = this._liveCollection[i];
		processedIndex = this._getElementProcessedIndex(elem);
		if (processedIndex !== undefined) {
			this._processedElems.push(elem);
			lastProcessedElems[processedIndex] = null;
		} else {
			addedElems.push(elem);
		}
	}
	removedElems = lastProcessedElems.filter(filterNullOut);
	
	// Call the callbacks and pass the corresponding bucket
	if (addedElems.length !== 0 && this._onAdd !== undefined) {
		this._onAdd(addedElems);
	}
	if (removedElems.length !== 0 && this._onRemove !== undefined) {
		for (var i = 0, len = removedElems.length; i < len; i++) {
			this._clearElementProcessedIndex(removedElems[i]);
		}
		this._onRemove(removedElems);
	}
	
	// Update this._processedElems and update the index stored on each element
	this._processedElems = this._processedElems.concat(addedElems);
	for (var i = 0, len = this._processedElems.length; i < len; i++) {
		this._setElementProcessedIndex(this._processedElems[i], i);
	}
}

function filterNullOut(item){ return item !== null; };

//====================================================================


module.exports = {
	getInstance : getInstance
};

